import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass';
import { X, Volume2, VolumeX, Play, Pause, Maximize2, RotateCcw, Zap, Music, Palette } from 'lucide-react';
import { Button } from './ui/button';

// Venue themes configuration
const VENUES = {
  obsidian: {
    name: 'Obsidian Void',
    description: 'Dark crystal realm',
    colors: { primary: 0xff0040, secondary: 0x00ffff, accent: 0x8000ff, bg: 0x000005 },
    fog: { color: 0x000005, density: 0.015 },
    bloomStrength: 1.5
  },
  cyberpunk: {
    name: 'Neon City',
    description: 'Cyberpunk metropolis',
    colors: { primary: 0xff00ff, secondary: 0x00ffff, accent: 0xffff00, bg: 0x0a0015 },
    fog: { color: 0x0a0015, density: 0.02 },
    bloomStrength: 2.0
  },
  underwater: {
    name: 'Abyss',
    description: 'Deep ocean depths',
    colors: { primary: 0x00ff88, secondary: 0x0088ff, accent: 0x00ffcc, bg: 0x000510 },
    fog: { color: 0x001020, density: 0.025 },
    bloomStrength: 1.2
  },
  interplanetary: {
    name: 'Cosmic Void',
    description: 'Interstellar space',
    colors: { primary: 0xff6600, secondary: 0x9900ff, accent: 0xff0066, bg: 0x020010 },
    fog: { color: 0x020010, density: 0.008 },
    bloomStrength: 1.8
  },
  lasershow: {
    name: 'Laser Matrix',
    description: 'Pure light show',
    colors: { primary: 0xff0000, secondary: 0x00ff00, accent: 0x0000ff, bg: 0x000000 },
    fog: { color: 0x000000, density: 0.01 },
    bloomStrength: 2.5
  }
};

// Main Virtual Concert Component
export const VirtualConcert = ({ track, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentVenue, setCurrentVenue] = useState('obsidian');
  const [showVenueSelector, setShowVenueSelector] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [concertAudioUrl, setConcertAudioUrl] = useState(null);
  const [currentTrackTitle, setCurrentTrackTitle] = useState(track?.title || 'REDACTED - Live');
  const containerRef = useRef(null);
  const canvasContainerRef = useRef(null);
  const sceneDataRef = useRef(null);
  const audioRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);

  // Fetch active concert audio from backend
  useEffect(() => {
    const fetchConcertAudio = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/uploads/active/concert_audio`);
        const data = await response.json();
        
        if (data.media && data.media.url) {
          // Construct full URL for the audio file
          // Use /api/uploads/files/ route for Kubernetes ingress routing
          const filename = data.media.url.split('/').pop();
          const audioUrl = `${API_URL}/api/uploads/files/${filename}`;
          setConcertAudioUrl(audioUrl);
          setCurrentTrackTitle(data.media.title || track?.title || 'REDACTED - Live');
          console.log('Concert audio loaded:', audioUrl);
        } else {
          // Fallback to placeholder if no active concert audio
          console.log('No active concert audio found, using fallback');
          setConcertAudioUrl("https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3");
        }
      } catch (error) {
        console.error('Error fetching concert audio:', error);
        setConcertAudioUrl("https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3");
      }
    };

    fetchConcertAudio();
  }, [track?.title]);

  // Initialize Web Audio API
  const initAudio = useCallback(() => {
    if (audioContextRef.current) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    audioContextRef.current = audioContext;

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.8;
    analyserRef.current = analyser;

    const bufferLength = analyser.frequencyBinCount;
    dataArrayRef.current = new Uint8Array(bufferLength);

    if (audioRef.current) {
      const source = audioContext.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(audioContext.destination);
    }
  }, []);

  const getAudioData = useCallback(() => {
    if (!analyserRef.current || !dataArrayRef.current) {
      return new Array(64).fill(0).map(() => Math.random() * 0.5 + 0.25);
    }
    analyserRef.current.getByteFrequencyData(dataArrayRef.current);
    return Array.from(dataArrayRef.current).map(v => v / 255);
  }, []);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) return;
    
    if (!audioContextRef.current) {
      initAudio();
    }

    if (audioContextRef.current?.state === 'suspended') {
      audioContextRef.current.resume();
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(console.error);
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, initAudio]);

  const toggleMute = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
    setIsMuted(!isMuted);
  }, [isMuted]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const resetCamera = () => {
    if (sceneDataRef.current) {
      sceneDataRef.current.cameraControls.theta = 0;
      sceneDataRef.current.cameraControls.phi = Math.PI / 3;
      sceneDataRef.current.cameraControls.targetRadius = 25;
    }
  };

  const triggerGlitch = () => {
    if (sceneDataRef.current) {
      sceneDataRef.current.glitchIntensity = 1;
    }
  };

  const changeVenue = (venueId) => {
    setCurrentVenue(venueId);
    setShowVenueSelector(false);
    if (sceneDataRef.current) {
      sceneDataRef.current.changeVenue(venueId);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => {
      document.body.style.overflow = 'unset';
      clearTimeout(timer);
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Three.js Scene Setup
  useEffect(() => {
    if (isLoading || !canvasContainerRef.current) return;

    const container = canvasContainerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;
    let venue = VENUES[currentVenue];

    // Scene
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(venue.fog.color, venue.fog.density);

    // Camera
    const camera = new THREE.PerspectiveCamera(70, width / height, 0.1, 1000);
    camera.position.set(0, 8, 25);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(venue.colors.bg);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);

    // Post-processing
    const composer = new EffectComposer(renderer);
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);

    const bloomPass = new UnrealBloomPass(
      new THREE.Vector2(width, height),
      venue.bloomStrength, 0.4, 0.85
    );
    composer.addPass(bloomPass);

    // Camera Controls
    const cameraControls = {
      theta: 0, phi: Math.PI / 3, radius: 25, targetRadius: 25,
      isDragging: false, previousMouse: { x: 0, y: 0 }, autoRotate: true
    };

    let glitchIntensity = 0;

    // Mouse Controls
    const handleMouseDown = (e) => {
      cameraControls.isDragging = true;
      cameraControls.autoRotate = false;
      cameraControls.previousMouse = { x: e.clientX, y: e.clientY };
    };
    const handleMouseUp = () => {
      cameraControls.isDragging = false;
      setTimeout(() => { cameraControls.autoRotate = true; }, 3000);
    };
    const handleMouseMove = (e) => {
      if (!cameraControls.isDragging) return;
      cameraControls.theta -= (e.clientX - cameraControls.previousMouse.x) * 0.005;
      cameraControls.phi = Math.max(0.2, Math.min(Math.PI / 2 - 0.1, 
        cameraControls.phi + (e.clientY - cameraControls.previousMouse.y) * 0.005));
      cameraControls.previousMouse = { x: e.clientX, y: e.clientY };
    };
    const handleWheel = (e) => {
      cameraControls.targetRadius = Math.max(10, Math.min(50, cameraControls.targetRadius + e.deltaY * 0.03));
    };
    const handleTouchStart = (e) => {
      if (e.touches.length === 1) {
        cameraControls.isDragging = true;
        cameraControls.autoRotate = false;
        cameraControls.previousMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    };
    const handleTouchMove = (e) => {
      if (!cameraControls.isDragging || e.touches.length !== 1) return;
      cameraControls.theta -= (e.touches[0].clientX - cameraControls.previousMouse.x) * 0.005;
      cameraControls.phi = Math.max(0.2, Math.min(Math.PI / 2 - 0.1,
        cameraControls.phi + (e.touches[0].clientY - cameraControls.previousMouse.y) * 0.005));
      cameraControls.previousMouse = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };
    const handleTouchEnd = () => {
      cameraControls.isDragging = false;
      setTimeout(() => { cameraControls.autoRotate = true; }, 3000);
    };

    renderer.domElement.addEventListener('mousedown', handleMouseDown);
    renderer.domElement.addEventListener('mouseup', handleMouseUp);
    renderer.domElement.addEventListener('mousemove', handleMouseMove);
    renderer.domElement.addEventListener('wheel', handleWheel);
    renderer.domElement.addEventListener('touchstart', handleTouchStart);
    renderer.domElement.addEventListener('touchmove', handleTouchMove);
    renderer.domElement.addEventListener('touchend', handleTouchEnd);

    // ===== DYNAMIC SCENE ELEMENTS =====
    const sceneElements = {
      lights: [], spotlights: [], bars: [], crystals: [], lasers: [],
      particles: [], bandMembers: [], specialObjects: [], rings: []
    };

    // Ambient light
    const ambientLight = new THREE.AmbientLight(0x111122, 0.3);
    scene.add(ambientLight);

    // Create stage (shared across venues)
    const createStage = (colors) => {
      const group = new THREE.Group();
      
      // Multi-level platform
      const levels = [
        { radius: 12, height: 0.8, y: -4 },
        { radius: 10, height: 0.6, y: -3.2 },
        { radius: 8, height: 0.4, y: -2.6 }
      ];
      levels.forEach((level, i) => {
        const geo = new THREE.CylinderGeometry(level.radius, level.radius + 1, level.height, 6);
        const mat = new THREE.MeshStandardMaterial({
          color: new THREE.Color(colors.bg).offsetHSL(0, 0, 0.05 + i * 0.02),
          metalness: 0.95, roughness: 0.1
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.y = level.y;
        group.add(mesh);
      });

      // Stage rings
      [colors.primary, colors.secondary, colors.primary].forEach((color, i) => {
        const ringGeo = new THREE.TorusGeometry(10 - i * 2, 0.08, 16, 100);
        const ringMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.rotation.x = -Math.PI / 2;
        ring.position.y = -2.2 + i * 0.1;
        group.add(ring);
        sceneElements.rings.push(ring);
      });

      scene.add(group);
      return group;
    };

    // Create audio visualizer bars
    const createBars = (colors, count = 48) => {
      sceneElements.bars.forEach(b => scene.remove(b.mesh));
      sceneElements.bars = [];
      
      const barColors = [colors.primary, colors.secondary, colors.accent];
      for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2;
        const radius = 11;
        const geo = new THREE.BoxGeometry(0.3, 1, 0.3);
        const color = barColors[i % 3];
        const mat = new THREE.MeshStandardMaterial({
          color, emissive: color, emissiveIntensity: 0.6, metalness: 0.8, roughness: 0.2
        });
        const bar = new THREE.Mesh(geo, mat);
        bar.position.set(Math.cos(angle) * radius, -3, Math.sin(angle) * radius);
        bar.rotation.y = -angle;
        scene.add(bar);
        sceneElements.bars.push({ mesh: bar, baseY: -3, index: i });
      }
    };

    // Create energy orb
    const createOrb = (colors) => {
      const group = new THREE.Group();
      
      const coreGeo = new THREE.IcosahedronGeometry(1.2, 3);
      const coreMat = new THREE.MeshStandardMaterial({
        color: 0x100008, emissive: colors.primary, emissiveIntensity: 0.8, metalness: 1, roughness: 0
      });
      const core = new THREE.Mesh(coreGeo, coreMat);
      group.add(core);

      const shellGeo = new THREE.IcosahedronGeometry(1.8, 1);
      const shellMat = new THREE.MeshBasicMaterial({
        color: colors.primary, transparent: true, opacity: 0.15, wireframe: true
      });
      const shell = new THREE.Mesh(shellGeo, shellMat);
      group.add(shell);

      for (let i = 0; i < 3; i++) {
        const ringGeo = new THREE.TorusGeometry(2 + i * 0.3, 0.02, 16, 100);
        const ringMat = new THREE.MeshBasicMaterial({
          color: i === 1 ? colors.secondary : colors.primary, transparent: true, opacity: 0.8
        });
        const ring = new THREE.Mesh(ringGeo, ringMat);
        ring.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
        group.add(ring);
      }

      const light = new THREE.PointLight(colors.primary, 20, 30);
      group.add(light);

      group.position.y = 5;
      scene.add(group);
      sceneElements.specialObjects.push({ type: 'orb', group, core, shell });
      return group;
    };

    // Create band members
    const createBandMembers = (colors) => {
      sceneElements.bandMembers.forEach(m => scene.remove(m.group));
      sceneElements.bandMembers = [];

      const positions = [[-4, -1.5, -2], [4, -1.5, -2], [0, -1, -4], [0, -0.5, 2], [-6, -1.5, 1], [6, -1.5, 1]];
      positions.forEach((pos, i) => {
        const group = new THREE.Group();
        const color = i === 3 ? colors.primary : colors.secondary;
        
        const bodyGeo = new THREE.CapsuleGeometry(0.4, 1.2, 4, 8);
        const bodyMat = new THREE.MeshStandardMaterial({
          color, transparent: true, opacity: 0.7, emissive: color, emissiveIntensity: 0.5
        });
        const body = new THREE.Mesh(bodyGeo, bodyMat);
        group.add(body);

        const headGeo = new THREE.SphereGeometry(0.3, 16, 16);
        const head = new THREE.Mesh(headGeo, bodyMat.clone());
        head.position.y = 1.1;
        group.add(head);

        const scanGeo = new THREE.PlaneGeometry(1.2, 0.03);
        const scanMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.8 });
        const scan = new THREE.Mesh(scanGeo, scanMat);
        group.add(scan);

        const light = new THREE.PointLight(color, 2, 5);
        light.position.y = -0.5;
        group.add(light);

        group.position.set(...pos);
        scene.add(group);
        sceneElements.bandMembers.push({ group, body, scan, baseY: pos[1] });
      });
    };

    // Create spotlights
    const createSpotlights = (colors) => {
      sceneElements.spotlights.forEach(s => { scene.remove(s.light); scene.remove(s.light.target); });
      sceneElements.spotlights = [];

      const spotColors = [colors.primary, colors.secondary, colors.accent, colors.primary, colors.secondary];
      spotColors.forEach((color, i) => {
        const spot = new THREE.SpotLight(color, 50, 60, Math.PI / 6, 0.5, 2);
        const angle = (i / spotColors.length) * Math.PI * 2;
        spot.position.set(Math.cos(angle) * 20, 15, Math.sin(angle) * 20);
        spot.target.position.set(0, 0, 0);
        scene.add(spot);
        scene.add(spot.target);
        sceneElements.spotlights.push({ light: spot, baseAngle: angle });
      });
    };

    // Create lasers
    const createLasers = (colors, count = 12) => {
      sceneElements.lasers.forEach(l => scene.remove(l.mesh));
      sceneElements.lasers = [];

      const laserColors = [colors.primary, colors.secondary, colors.accent];
      for (let i = 0; i < count; i++) {
        const geo = new THREE.CylinderGeometry(0.015, 0.015, 30, 8);
        const mat = new THREE.MeshBasicMaterial({
          color: laserColors[i % 3], transparent: true, opacity: 0.9
        });
        const laser = new THREE.Mesh(geo, mat);
        const angle = (i / count) * Math.PI * 2;
        laser.position.set(Math.cos(angle) * 8, 10, Math.sin(angle) * 8);
        scene.add(laser);
        sceneElements.lasers.push({ mesh: laser, baseAngle: angle, index: i });
      }
    };

    // Create floating objects based on venue
    const createFloatingObjects = (venueId, colors) => {
      sceneElements.crystals.forEach(c => scene.remove(c.group));
      sceneElements.crystals = [];

      const count = venueId === 'lasershow' ? 20 : 60;
      
      for (let i = 0; i < count; i++) {
        const group = new THREE.Group();
        let geometry, size;

        switch(venueId) {
          case 'cyberpunk':
            // Holographic cubes and pyramids
            size = Math.random() * 0.8 + 0.3;
            geometry = i % 2 === 0 
              ? new THREE.BoxGeometry(size, size, size)
              : new THREE.TetrahedronGeometry(size);
            break;
          case 'underwater':
            // Organic bubble-like shapes
            size = Math.random() * 0.6 + 0.2;
            geometry = new THREE.SphereGeometry(size, 8, 8);
            break;
          case 'interplanetary':
            // Asteroids and space rocks
            size = Math.random() * 1 + 0.3;
            geometry = new THREE.DodecahedronGeometry(size, 0);
            break;
          case 'lasershow':
            // Geometric prisms
            size = Math.random() * 0.5 + 0.2;
            geometry = new THREE.ConeGeometry(size, size * 2, 4);
            break;
          default: // obsidian
            size = Math.random() * 0.5 + 0.2;
            geometry = new THREE.OctahedronGeometry(size, 0);
        }

        const objMat = new THREE.MeshStandardMaterial({
          color: venueId === 'underwater' ? 0x88ffff : 0x050508,
          metalness: 0.95, roughness: 0.05,
          transparent: venueId === 'underwater', opacity: venueId === 'underwater' ? 0.6 : 1
        });
        const obj = new THREE.Mesh(geometry, objMat);
        group.add(obj);

        // Wireframe glow
        const wireGeo = geometry.clone();
        wireGeo.scale(1.1, 1.1, 1.1);
        const glowColors = [colors.primary, colors.secondary, colors.accent, 0x00ff80];
        const wireMat = new THREE.MeshBasicMaterial({
          color: glowColors[i % 4], transparent: true, opacity: 0.3, wireframe: true
        });
        const wire = new THREE.Mesh(wireGeo, wireMat);
        group.add(wire);

        const light = new THREE.PointLight(glowColors[i % 4], 0.5, 3);
        group.add(light);

        const spread = venueId === 'lasershow' ? 30 : 50;
        group.position.set(
          (Math.random() - 0.5) * spread,
          Math.random() * 25 - 5,
          (Math.random() - 0.5) * spread
        );

        scene.add(group);
        sceneElements.crystals.push({
          group, speed: Math.random() * 0.02 + 0.005,
          floatSpeed: Math.random() * 0.5 + 0.2,
          floatOffset: Math.random() * Math.PI * 2
        });
      }
    };

    // Create particles based on venue
    const createParticles = (venueId, colors) => {
      sceneElements.particles.forEach(p => scene.remove(p));
      sceneElements.particles = [];

      // Stars (all venues)
      const starCount = venueId === 'underwater' ? 500 : 5000;
      const starGeo = new THREE.BufferGeometry();
      const starPos = new Float32Array(starCount * 3);
      for (let i = 0; i < starCount; i++) {
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(Math.random() * 2 - 1);
        const r = 100 + Math.random() * 50;
        starPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
        starPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
        starPos[i * 3 + 2] = r * Math.cos(phi);
      }
      starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
      const starMat = new THREE.PointsMaterial({
        size: venueId === 'interplanetary' ? 0.8 : 0.4,
        color: venueId === 'underwater' ? 0x88ffff : 0xffffff,
        transparent: true, opacity: 0.8
      });
      const stars = new THREE.Points(starGeo, starMat);
      scene.add(stars);
      sceneElements.particles.push(stars);

      // Venue-specific particles
      if (venueId === 'cyberpunk') {
        // Rain effect
        const rainCount = 2000;
        const rainGeo = new THREE.BufferGeometry();
        const rainPos = new Float32Array(rainCount * 3);
        for (let i = 0; i < rainCount; i++) {
          rainPos[i * 3] = (Math.random() - 0.5) * 60;
          rainPos[i * 3 + 1] = Math.random() * 40 - 5;
          rainPos[i * 3 + 2] = (Math.random() - 0.5) * 60;
        }
        rainGeo.setAttribute('position', new THREE.BufferAttribute(rainPos, 3));
        const rainMat = new THREE.PointsMaterial({
          size: 0.1, color: 0x00ffff, transparent: true, opacity: 0.5
        });
        const rain = new THREE.Points(rainGeo, rainMat);
        rain.userData = { type: 'rain', velocities: rainPos.map(() => Math.random() * 0.3 + 0.2) };
        scene.add(rain);
        sceneElements.particles.push(rain);
      }

      if (venueId === 'underwater') {
        // Bubbles
        const bubbleCount = 500;
        const bubbleGeo = new THREE.BufferGeometry();
        const bubblePos = new Float32Array(bubbleCount * 3);
        for (let i = 0; i < bubbleCount; i++) {
          bubblePos[i * 3] = (Math.random() - 0.5) * 50;
          bubblePos[i * 3 + 1] = Math.random() * 30 - 10;
          bubblePos[i * 3 + 2] = (Math.random() - 0.5) * 50;
        }
        bubbleGeo.setAttribute('position', new THREE.BufferAttribute(bubblePos, 3));
        const bubbleMat = new THREE.PointsMaterial({
          size: 0.3, color: 0xffffff, transparent: true, opacity: 0.6,
          blending: THREE.AdditiveBlending
        });
        const bubbles = new THREE.Points(bubbleGeo, bubbleMat);
        bubbles.userData = { type: 'bubbles' };
        scene.add(bubbles);
        sceneElements.particles.push(bubbles);
      }

      if (venueId === 'interplanetary') {
        // Cosmic dust
        const dustCount = 3000;
        const dustGeo = new THREE.BufferGeometry();
        const dustPos = new Float32Array(dustCount * 3);
        const dustColors = new Float32Array(dustCount * 3);
        for (let i = 0; i < dustCount; i++) {
          dustPos[i * 3] = (Math.random() - 0.5) * 80;
          dustPos[i * 3 + 1] = (Math.random() - 0.5) * 80;
          dustPos[i * 3 + 2] = (Math.random() - 0.5) * 80;
          const c = new THREE.Color().setHSL(Math.random() * 0.3 + 0.7, 0.8, 0.5);
          dustColors[i * 3] = c.r;
          dustColors[i * 3 + 1] = c.g;
          dustColors[i * 3 + 2] = c.b;
        }
        dustGeo.setAttribute('position', new THREE.BufferAttribute(dustPos, 3));
        dustGeo.setAttribute('color', new THREE.BufferAttribute(dustColors, 3));
        const dustMat = new THREE.PointsMaterial({
          size: 0.2, vertexColors: true, transparent: true, opacity: 0.6,
          blending: THREE.AdditiveBlending
        });
        const dust = new THREE.Points(dustGeo, dustMat);
        dust.userData = { type: 'dust' };
        scene.add(dust);
        sceneElements.particles.push(dust);
      }

      // Embers (all except underwater)
      if (venueId !== 'underwater') {
        const emberCount = 500;
        const emberGeo = new THREE.BufferGeometry();
        const emberPos = new Float32Array(emberCount * 3);
        const emberVel = [];
        for (let i = 0; i < emberCount; i++) {
          emberPos[i * 3] = (Math.random() - 0.5) * 40;
          emberPos[i * 3 + 1] = Math.random() * 30 - 5;
          emberPos[i * 3 + 2] = (Math.random() - 0.5) * 40;
          emberVel.push({ x: (Math.random() - 0.5) * 0.02, y: Math.random() * 0.05 + 0.02, z: (Math.random() - 0.5) * 0.02 });
        }
        emberGeo.setAttribute('position', new THREE.BufferAttribute(emberPos, 3));
        const emberMat = new THREE.PointsMaterial({
          size: 0.1, color: colors.primary, transparent: true, opacity: 0.8,
          blending: THREE.AdditiveBlending
        });
        const embers = new THREE.Points(emberGeo, emberMat);
        embers.userData = { type: 'embers', velocities: emberVel };
        scene.add(embers);
        sceneElements.particles.push(embers);
      }

      // Crowd
      const crowdCount = 1000;
      const crowdGeo = new THREE.BufferGeometry();
      const crowdPos = new Float32Array(crowdCount * 3);
      for (let i = 0; i < crowdCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        const radius = 18 + Math.random() * 15;
        crowdPos[i * 3] = Math.cos(angle) * radius;
        crowdPos[i * 3 + 1] = -4 + Math.random() * 0.5;
        crowdPos[i * 3 + 2] = Math.sin(angle) * radius;
      }
      crowdGeo.setAttribute('position', new THREE.BufferAttribute(crowdPos, 3));
      const crowdMat = new THREE.PointsMaterial({
        size: 0.3, color: 0x444466, transparent: true, opacity: 0.6
      });
      const crowd = new THREE.Points(crowdGeo, crowdMat);
      crowd.userData = { type: 'crowd' };
      scene.add(crowd);
      sceneElements.particles.push(crowd);
    };

    // Create LED screens
    const createScreens = (colors) => {
      const positions = [
        { pos: [0, 8, -18], rot: [0, 0, 0], size: [20, 10] },
        { pos: [-18, 6, 0], rot: [0, Math.PI / 2, 0], size: [14, 8] },
        { pos: [18, 6, 0], rot: [0, -Math.PI / 2, 0], size: [14, 8] }
      ];

      positions.forEach(({ pos, rot, size }) => {
        const frameGeo = new THREE.BoxGeometry(size[0] + 0.5, size[1] + 0.5, 0.3);
        const frameMat = new THREE.MeshStandardMaterial({ color: 0x111111, metalness: 0.9, roughness: 0.2 });
        const frame = new THREE.Mesh(frameGeo, frameMat);
        frame.position.set(...pos);
        frame.rotation.set(...rot);
        scene.add(frame);

        const screenGeo = new THREE.PlaneGeometry(size[0], size[1]);
        const screenMat = new THREE.MeshBasicMaterial({ color: colors.primary, transparent: true, opacity: 0.3 });
        const screen = new THREE.Mesh(screenGeo, screenMat);
        screen.position.set(pos[0], pos[1], pos[2] + (rot[1] === 0 ? 0.2 : 0));
        if (rot[1] !== 0) screen.position.x += rot[1] > 0 ? 0.2 : -0.2;
        screen.rotation.set(...rot);
        scene.add(screen);
        sceneElements.specialObjects.push({ type: 'screen', mesh: screen, material: screenMat });
      });
    };

    // Create band name
    const createBandName = (colors) => {
      const group = new THREE.Group();
      const letters = 'REDACTED'.split('');
      const spacing = 2.2;
      const startX = -((letters.length - 1) * spacing) / 2;

      letters.forEach((_, i) => {
        const geo = new THREE.BoxGeometry(1.8, 2.5, 0.4);
        const mat = new THREE.MeshStandardMaterial({
          color: colors.primary, emissive: colors.primary, emissiveIntensity: 1, metalness: 0.9, roughness: 0.1
        });
        const block = new THREE.Mesh(geo, mat);
        block.position.x = startX + i * spacing;
        group.add(block);
      });

      const light = new THREE.PointLight(colors.primary, 10, 25);
      light.position.z = 3;
      group.add(light);

      group.position.set(0, 14, -16);
      scene.add(group);
      sceneElements.specialObjects.push({ type: 'bandName', group });
    };

    // Create halo rings
    const createHalos = (colors) => {
      for (let i = 0; i < 3; i++) {
        const geo = new THREE.TorusGeometry(14 + i * 2, 0.05, 16, 100);
        const mat = new THREE.MeshBasicMaterial({
          color: i === 1 ? colors.secondary : colors.primary, transparent: true, opacity: 0.7
        });
        const halo = new THREE.Mesh(geo, mat);
        halo.position.y = 3 + i * 0.5;
        scene.add(halo);
        sceneElements.specialObjects.push({ 
          type: 'halo', mesh: halo, 
          speed: 0.02 + i * 0.01, 
          direction: i % 2 === 0 ? 1 : -1 
        });
      }
    };

    // Build venue function
    const buildVenue = (venueId) => {
      venue = VENUES[venueId];
      const colors = venue.colors;

      // Update renderer and fog
      renderer.setClearColor(colors.bg);
      scene.fog = new THREE.FogExp2(venue.fog.color, venue.fog.density);
      bloomPass.strength = venue.bloomStrength;

      // Create all elements
      createStage(colors);
      createBars(colors, venueId === 'lasershow' ? 64 : 48);
      createOrb(colors);
      createBandMembers(colors);
      createSpotlights(colors);
      createLasers(colors, venueId === 'lasershow' ? 24 : 12);
      createFloatingObjects(venueId, colors);
      createParticles(venueId, colors);
      createScreens(colors);
      createBandName(colors);
      createHalos(colors);
    };

    // Initial build
    buildVenue(currentVenue);

    // Change venue function
    const changeVenueInternal = (venueId) => {
      // Clear existing elements
      sceneElements.bars.forEach(b => scene.remove(b.mesh));
      sceneElements.crystals.forEach(c => scene.remove(c.group));
      sceneElements.lasers.forEach(l => scene.remove(l.mesh));
      sceneElements.particles.forEach(p => scene.remove(p));
      sceneElements.bandMembers.forEach(m => scene.remove(m.group));
      sceneElements.spotlights.forEach(s => { scene.remove(s.light); scene.remove(s.light.target); });
      sceneElements.specialObjects.forEach(o => {
        if (o.group) scene.remove(o.group);
        if (o.mesh) scene.remove(o.mesh);
      });
      sceneElements.rings.forEach(r => scene.remove(r));

      // Reset arrays
      Object.keys(sceneElements).forEach(key => sceneElements[key] = []);

      // Rebuild
      buildVenue(venueId);
    };

    // Animation loop
    const clock = new THREE.Clock();
    let frameId;

    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const time = clock.getElapsedTime();

      // Get audio data
      const audioData = getAudioData();
      const bassLevel = audioData.slice(0, 4).reduce((a, b) => a + b, 0) / 4;
      const midLevel = audioData.slice(4, 16).reduce((a, b) => a + b, 0) / 12;
      const highLevel = audioData.slice(16, 32).reduce((a, b) => a + b, 0) / 16;

      // Glitch decay
      if (glitchIntensity > 0) glitchIntensity *= 0.95;

      // Camera
      if (cameraControls.autoRotate) cameraControls.theta += 0.003;
      cameraControls.radius += (cameraControls.targetRadius - cameraControls.radius) * 0.05;

      const camX = cameraControls.radius * Math.sin(cameraControls.phi) * Math.cos(cameraControls.theta);
      const camY = cameraControls.radius * Math.cos(cameraControls.phi) + 3;
      const camZ = cameraControls.radius * Math.sin(cameraControls.phi) * Math.sin(cameraControls.theta);
      const shake = glitchIntensity * (Math.random() - 0.5) * 2;
      camera.position.set(camX + shake, camY + shake * 0.5, camZ + shake);
      camera.lookAt(0, 2, 0);

      // Audio-reactive visualizer bars
      sceneElements.bars.forEach((bar, i) => {
        const audioIndex = Math.floor((i / sceneElements.bars.length) * audioData.length);
        const freq = audioData[audioIndex] || (Math.sin(time * 4 + i * 0.3) * 0.5 + 0.5);
        const height = freq * 5 + 0.3;
        bar.mesh.scale.y = height;
        bar.mesh.position.y = bar.baseY + height / 2;
        bar.mesh.material.emissiveIntensity = 0.3 + freq * 0.7;
      });

      // Spotlights
      sceneElements.spotlights.forEach((s, i) => {
        const newAngle = s.baseAngle + time * 0.2;
        s.light.position.x = Math.cos(newAngle) * 20;
        s.light.position.z = Math.sin(newAngle) * 20;
        s.light.intensity = 30 + bassLevel * 50 + Math.sin(time * 2 + i) * 20;
      });

      // Lasers
      sceneElements.lasers.forEach((l, i) => {
        l.mesh.rotation.z = Math.sin(time * 2 + l.index * 0.5) * 0.8;
        l.mesh.rotation.x = Math.cos(time * 1.5 + l.index * 0.3) * 0.5;
        l.mesh.material.opacity = 0.5 + highLevel * 0.5;
      });

      // Floating objects
      sceneElements.crystals.forEach((c) => {
        c.group.rotation.y += c.speed;
        c.group.position.y += Math.sin(time * c.floatSpeed + c.floatOffset) * 0.005;
      });

      // Band members
      sceneElements.bandMembers.forEach((m, i) => {
        m.body.material.opacity = 0.5 + midLevel * 0.3 + Math.sin(time * 8 + i) * 0.1;
        m.scan.position.y = (time * 2 + i) % 3 - 1.5;
        m.group.position.y = m.baseY + bassLevel * 0.3;
      });

      // Special objects
      sceneElements.specialObjects.forEach(obj => {
        if (obj.type === 'orb') {
          obj.group.rotation.y += 0.01;
          obj.core.rotation.x = Math.sin(time) * 0.3;
          obj.shell.rotation.x -= 0.02;
          const scale = 1 + bassLevel * 0.3 + Math.sin(time * 2) * 0.1;
          obj.group.scale.set(scale, scale, scale);
          obj.group.position.y = 5 + Math.sin(time * 0.5) * 0.5;
        }
        if (obj.type === 'halo') {
          obj.mesh.rotation.y += obj.speed * obj.direction;
          obj.mesh.rotation.x = Math.sin(time * 0.3) * 0.2;
        }
        if (obj.type === 'screen') {
          const hue = (time * 0.1) % 1;
          obj.material.color.setHSL(hue, 0.8, 0.2 + bassLevel * 0.3);
        }
        if (obj.type === 'bandName') {
          obj.group.position.y = 14 + Math.sin(time * 0.5) * 0.3 + bassLevel * 0.5;
          obj.group.children.forEach((child, i) => {
            if (child.material?.emissiveIntensity !== undefined) {
              child.material.emissiveIntensity = 0.5 + highLevel + Math.sin(time * 3 + i * 0.2) * 0.3;
            }
          });
        }
      });

      // Particles
      sceneElements.particles.forEach(p => {
        const pos = p.geometry.attributes.position.array;
        
        if (p.userData.type === 'embers') {
          const vel = p.userData.velocities;
          for (let i = 0; i < pos.length / 3; i++) {
            pos[i * 3] += vel[i].x;
            pos[i * 3 + 1] += vel[i].y + bassLevel * 0.02;
            pos[i * 3 + 2] += vel[i].z;
            if (pos[i * 3 + 1] > 25) {
              pos[i * 3] = (Math.random() - 0.5) * 20;
              pos[i * 3 + 1] = -5;
              pos[i * 3 + 2] = (Math.random() - 0.5) * 20;
            }
          }
          p.geometry.attributes.position.needsUpdate = true;
        }

        if (p.userData.type === 'rain') {
          for (let i = 0; i < pos.length / 3; i++) {
            pos[i * 3 + 1] -= 0.3;
            if (pos[i * 3 + 1] < -5) {
              pos[i * 3 + 1] = 35;
            }
          }
          p.geometry.attributes.position.needsUpdate = true;
        }

        if (p.userData.type === 'bubbles') {
          for (let i = 0; i < pos.length / 3; i++) {
            pos[i * 3] += Math.sin(time + i) * 0.01;
            pos[i * 3 + 1] += 0.02 + bassLevel * 0.02;
            pos[i * 3 + 2] += Math.cos(time + i) * 0.01;
            if (pos[i * 3 + 1] > 20) {
              pos[i * 3 + 1] = -10;
            }
          }
          p.geometry.attributes.position.needsUpdate = true;
        }

        if (p.userData.type === 'dust') {
          p.rotation.y += 0.001;
        }

        if (p.userData.type === 'crowd') {
          for (let i = 0; i < pos.length / 3; i++) {
            pos[i * 3 + 1] = -4 + Math.sin(time * 4 + i * 0.1) * (0.3 + bassLevel * 0.5);
          }
          p.geometry.attributes.position.needsUpdate = true;
        }
      });

      composer.render();
    };

    animate();

    // Store reference
    sceneDataRef.current = { cameraControls, glitchIntensity, changeVenue: changeVenueInternal };

    // Resize handler
    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
      composer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(frameId);
      renderer.domElement.removeEventListener('mousedown', handleMouseDown);
      renderer.domElement.removeEventListener('mouseup', handleMouseUp);
      renderer.domElement.removeEventListener('mousemove', handleMouseMove);
      renderer.domElement.removeEventListener('wheel', handleWheel);
      renderer.domElement.removeEventListener('touchstart', handleTouchStart);
      renderer.domElement.removeEventListener('touchmove', handleTouchMove);
      renderer.domElement.removeEventListener('touchend', handleTouchEnd);
      renderer.dispose();
      composer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [isLoading, getAudioData]);

  const venueConfig = VENUES[currentVenue];

  return (
    <div ref={containerRef} className="fixed inset-0 z-50 bg-black" data-testid="virtual-concert-container">
      {/* Hidden Audio Element */}
      {concertAudioUrl && (
        <audio
          ref={audioRef}
          src={concertAudioUrl}
          loop
          preload="auto"
          onCanPlayThrough={() => setAudioLoaded(true)}
          onError={(e) => console.error('Audio load error:', e)}
          crossOrigin="anonymous"
        />
      )}

      {/* Loading Screen */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black z-50">
          <div className="text-center">
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 border-4 border-red-500/30 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div>
              <div className="absolute inset-2 border-4 border-cyan-500/30 rounded-full"></div>
              <div className="absolute inset-2 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}></div>
            </div>
            <h2 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-purple-500 to-cyan-500 mb-2">
              ENTERING THE VOID
            </h2>
            <p className="text-gray-500 text-sm tracking-widest">INITIALIZING {venueConfig.name.toUpperCase()}</p>
          </div>
        </div>
      )}

      {/* 3D Canvas Container */}
      <div ref={canvasContainerRef} className="absolute inset-0" />

      {/* Venue Selector Modal */}
      {showVenueSelector && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div className="bg-gray-900/90 rounded-2xl p-6 border border-gray-700 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-black text-white">SELECT VENUE</h3>
              <Button
                onClick={() => setShowVenueSelector(false)}
                variant="ghost"
                size="icon"
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(VENUES).map(([id, config]) => (
                <button
                  key={id}
                  onClick={() => changeVenue(id)}
                  className={`p-4 rounded-xl border-2 transition-all duration-300 text-left ${
                    currentVenue === id
                      ? 'border-red-500 bg-red-500/20'
                      : 'border-gray-700 hover:border-gray-500 bg-gray-800/50'
                  }`}
                >
                  <div 
                    className="w-full h-3 rounded-full mb-3"
                    style={{
                      background: `linear-gradient(90deg, #${config.colors.primary.toString(16).padStart(6, '0')}, #${config.colors.secondary.toString(16).padStart(6, '0')}, #${config.colors.accent.toString(16).padStart(6, '0')})`
                    }}
                  />
                  <h4 className="font-bold text-white text-lg">{config.name}</h4>
                  <p className="text-gray-400 text-sm">{config.description}</p>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* UI Overlay */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 p-4 flex justify-between items-start pointer-events-auto">
          {/* Track info */}
          <div className="bg-black/70 backdrop-blur-md rounded-xl p-4 border border-red-500/30 max-w-sm">
            <div className="flex items-center space-x-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${isPlaying ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
              <p className="text-red-500 font-bold text-xs uppercase tracking-widest">
                {isPlaying ? 'NOW PLAYING' : 'PAUSED'}
              </p>
            </div>
            <h3 className="text-2xl font-black text-white mb-1">{currentTrackTitle}</h3>
            <p className="text-gray-400 text-sm">{venueConfig.name} Experience</p>
          </div>

          {/* Top right buttons */}
          <div className="flex space-x-2">
            <Button
              onClick={() => setShowVenueSelector(true)}
              variant="ghost"
              size="icon"
              className="bg-black/70 backdrop-blur-md hover:bg-purple-500 text-white rounded-full w-12 h-12 border border-purple-500/30 transition-all duration-300 hover:scale-110"
              data-testid="venue-selector-btn"
            >
              <Palette size={24} />
            </Button>
            <Button
              onClick={onClose}
              variant="ghost"
              size="icon"
              className="bg-black/70 backdrop-blur-md hover:bg-red-500 text-white rounded-full w-12 h-12 border border-red-500/30 transition-all duration-300 hover:scale-110"
              data-testid="close-concert-btn"
            >
              <X size={24} />
            </Button>
          </div>
        </div>

        {/* Side info */}
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <div className="writing-mode-vertical text-gray-600 text-xs tracking-[0.3em] font-bold opacity-50">
            MINDESIGN × REDACTED × {venueConfig.name.toUpperCase()}
          </div>
        </div>

        {/* Bottom controls */}
        <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-auto">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-4">
              <p className="text-gray-400 text-sm">
                <span className="text-red-500 font-bold">DRAG</span> to explore | 
                <span className="text-cyan-400 font-bold"> SCROLL</span> to zoom | 
                <span className="text-purple-400 font-bold"> {venueConfig.name}</span>
              </p>
            </div>

            <div className="bg-black/70 backdrop-blur-md rounded-2xl p-4 border border-gray-700/30">
              <div className="flex justify-center items-center space-x-3">
                <Button
                  onClick={toggleMute}
                  variant="ghost"
                  size="icon"
                  className="bg-gray-900/80 hover:bg-gray-700 text-white rounded-full w-11 h-11 transition-all duration-300 hover:scale-110"
                  data-testid="mute-concert-btn"
                >
                  {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </Button>

                <Button
                  onClick={resetCamera}
                  variant="ghost"
                  size="icon"
                  className="bg-gray-900/80 hover:bg-gray-700 text-white rounded-full w-11 h-11 transition-all duration-300 hover:scale-110"
                  data-testid="reset-concert-btn"
                >
                  <RotateCcw size={20} />
                </Button>

                <Button
                  onClick={togglePlay}
                  variant="ghost"
                  size="icon"
                  className={`${isPlaying ? 'bg-green-500 hover:bg-green-600' : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500'} text-white rounded-full w-16 h-16 transition-all duration-300 hover:scale-110 shadow-lg ${isPlaying ? 'shadow-green-500/30' : 'shadow-red-500/30'}`}
                  data-testid="play-concert-btn"
                >
                  {isPlaying ? <Pause size={28} /> : <Play size={28} className="ml-1" />}
                </Button>

                <Button
                  onClick={triggerGlitch}
                  variant="ghost"
                  size="icon"
                  className="bg-gray-900/80 hover:bg-purple-600 text-white rounded-full w-11 h-11 transition-all duration-300 hover:scale-110"
                  data-testid="glitch-concert-btn"
                >
                  <Zap size={20} />
                </Button>

                <Button
                  onClick={toggleFullscreen}
                  variant="ghost"
                  size="icon"
                  className="bg-gray-900/80 hover:bg-gray-700 text-white rounded-full w-11 h-11 transition-all duration-300 hover:scale-110"
                  data-testid="fullscreen-concert-btn"
                >
                  <Maximize2 size={20} />
                </Button>
              </div>

              {/* Audio indicator */}
              {audioLoaded && (
                <div className="flex justify-center mt-3 space-x-1">
                  {[...Array(20)].map((_, i) => (
                    <div
                      key={i}
                      className="w-1 bg-gradient-to-t from-red-500 to-cyan-500 rounded-full transition-all duration-100"
                      style={{
                        height: isPlaying ? `${Math.random() * 20 + 5}px` : '5px',
                        opacity: isPlaying ? 1 : 0.3
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Watermark */}
        <div className="absolute bottom-4 right-4 pointer-events-none">
          <p className="text-gray-700 text-xs tracking-widest font-bold">POWERED BY MINDESIGN</p>
        </div>

        {/* Corner accents */}
        <div className="absolute top-0 left-0 w-24 h-24 border-l-2 border-t-2 border-red-500/40" />
        <div className="absolute top-0 right-0 w-24 h-24 border-r-2 border-t-2 border-cyan-400/40" />
        <div className="absolute bottom-0 left-0 w-24 h-24 border-l-2 border-b-2 border-purple-500/40" />
        <div className="absolute bottom-0 right-0 w-24 h-24 border-r-2 border-b-2 border-red-500/40" />

        {/* Scanlines */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-[0.03]"
          style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)' }}
        />
      </div>

      <style>{`
        .writing-mode-vertical { writing-mode: vertical-rl; text-orientation: mixed; }
      `}</style>
    </div>
  );
};
